import React, { useState, useEffect } from 'react';
import { syncPortfolio, getPositions } from '../api/portfolioApi';
import AccountSidebar from '../components/AccountSidebar';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import StockChartModal from '../components/StockChartModal';

const PortfolioPage = () => {
  const [positions, setPositions] = useState([]);
  const [summary, setSummary] = useState({
    total_investment: 0,
    total_value: 0,
    total_pnl: 0,
    total_pnl_percent: 0,
    total_day_pnl: 0,
    position_count: 0
  });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Chart modal states
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);
  const [selectedSymbolForChart, setSelectedSymbolForChart] = useState(null);

  // Fetch positions on component mount
  useEffect(() => {
    fetchPositions();
  }, []);

  const fetchPositions = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('--- DEBUG: PortfolioPage fetchPositions called ---');
      const response = await getPositions();
      console.log('--- DEBUG: PortfolioPage received response ---');
      console.log('Response:', response);
      if (response.success) {
        setPositions(response.positions);
        setSummary(response.summary);
      } else {
        setError('Failed to fetch portfolio data');
      }
    } catch (err) {
      console.error('--- DEBUG: Error caught in PortfolioPage component (fetchPositions) ---');
      console.error('Full error:', err);
      console.error('Error response:', err.response);
      console.error('Error status:', err.response?.status);
      console.error('Error data:', err.response?.data);
      setError(err.response?.data?.detail || 'Failed to fetch portfolio data');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    setSuccessMessage('');
    try {
      console.log('--- DEBUG: PortfolioPage handleSync called ---');
      const response = await syncPortfolio('ZERODHA');
      console.log('--- DEBUG: PortfolioPage received sync response ---');
      console.log('Response:', response);
      if (response.success) {
        setSuccessMessage(response.message);
        // Refresh positions after sync
        await fetchPositions();
      } else {
        setError(response.message || 'Sync failed');
      }
    } catch (err) {
      console.error('--- DEBUG: Error caught in PortfolioPage component (handleSync) ---');
      console.error('Full error:', err);
      console.error('Error response:', err.response);
      console.error('Error status:', err.response?.status);
      console.error('Error data:', err.response?.data);
      setError(err.response?.data?.detail || 'Failed to sync portfolio');
    } finally {
      setSyncing(false);
    }
  };

  const formatCurrency = (value) => {
    const safeValue = value || 0;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(safeValue);
  };

  const formatPercentage = (value) => {
    const safeValue = value || 0;
    return `${safeValue >= 0 ? '+' : ''}${safeValue.toFixed(2)}%`;
  };

  return (
    <div 
      className="min-h-screen font-karla flex flex-col"
      style={{
        backgroundImage: 'url(/assets/profilepage/profile_page_bg.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}
    >
      <Navbar />
      
      <div className="flex-1 flex items-center justify-center py-16 lg:py-12">
        <div className="max-w-6xl w-full mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="md:grid md:grid-cols-4">
              <AccountSidebar activePage="portfolio" />
              
              <div className="md:col-span-3">
                <div className="bg-white rounded-r-2xl p-8">
                  {/* Header */}
                  <div className="flex justify-between items-center mb-8">
                    <div>
                      <h1 className="text-3xl font-bold text-gray-900">Portfolio</h1>
                      <p className="text-gray-600 mt-1">Track your investments and performance</p>
                    </div>
                    <button
                      onClick={handleSync}
                      disabled={syncing}
                      className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2"
                    >
                      {syncing ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          <span>Syncing...</span>
                        </>
                      ) : (
                        <>
                          <span>🔄</span>
                          <span>Sync with Broker</span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* Success/Error Messages */}
                  {successMessage && (
                    <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg mb-6">
                      {successMessage}
                    </div>
                  )}
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-6">
                      {error}
                    </div>
                  )}

                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white rounded-lg shadow-md p-6">
                      <div className="text-gray-600 text-sm mb-2">Total Investment</div>
                      <div className="text-2xl font-bold text-gray-900">{formatCurrency(summary.total_investment || 0)}</div>
                    </div>
                    
                    <div className="bg-white rounded-lg shadow-md p-6">
                      <div className="text-gray-600 text-sm mb-2">Current Value</div>
                      <div className="text-2xl font-bold text-gray-900">{formatCurrency(summary.total_value || 0)}</div>
                    </div>
                    
                    <div className="bg-white rounded-lg shadow-md p-6">
                      <div className="text-gray-600 text-sm mb-2">Total P&L</div>
                      <div className={`text-2xl font-bold ${(summary.total_pnl || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(summary.total_pnl || 0)}
                        <span className="text-lg ml-2">
                          ({formatPercentage(summary.total_pnl_percent || 0)})
                        </span>
                      </div>
                    </div>
                    
                    <div className="bg-white rounded-lg shadow-md p-6">
                      <div className="text-gray-600 text-sm mb-2">Day's P&L</div>
                      <div className={`text-2xl font-bold ${(summary.total_day_pnl || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(summary.total_day_pnl || 0)}
                      </div>
                    </div>
                  </div>

                  {/* Positions Table */}
                  <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h2 className="text-xl font-semibold text-gray-900">Positions ({summary.position_count || 0})</h2>
                    </div>
                    
                    {loading ? (
                      <div className="p-12 text-center">
                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                        <p className="mt-4 text-gray-600">Loading positions...</p>
                      </div>
                    ) : positions.length === 0 ? (
                      <div className="p-12 text-center">
                        <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">No positions found</h3>
                        <p className="text-gray-600 mb-4">Sync with your broker to see your holdings</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Symbol
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Quantity
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Avg. Price
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Current Price
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Investment
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Current Value
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                P&L
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Day P&L
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {positions.map((position) => (
                              <tr key={position.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <button
                                    onClick={() => {
                                      setSelectedSymbolForChart(position.stock_symbol);
                                      setIsChartModalOpen(true);
                                    }}
                                    className="text-blue-600 hover:text-blue-800 font-semibold hover:underline cursor-pointer text-left"
                                    title="Click to view chart"
                                  >
                                    {position.stock_symbol}
                                  </button>
                                  <div className="text-xs text-gray-500">{position.exchange}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {position.quantity || 0}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {formatCurrency(position.average_price || 0)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {formatCurrency(position.current_price || 0)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {formatCurrency(position.investment_value || 0)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {formatCurrency(position.current_value || 0)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                  <span className={`font-semibold ${(position.unrealized_pnl || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {formatCurrency(position.unrealized_pnl || 0)}
                                    <span className="ml-2 text-xs">
                                      ({formatPercentage(position.unrealized_pnl_percent || 0)})
                                    </span>
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                  <span className={`font-semibold ${(position.day_pnl || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {formatCurrency(position.day_pnl || 0)}
                                    <span className="ml-2 text-xs">
                                      ({formatPercentage(position.day_pnl_percent || 0)})
                                    </span>
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />

      {/* Stock Chart Modal */}
      <StockChartModal
        isOpen={isChartModalOpen}
        onClose={() => setIsChartModalOpen(false)}
        symbol={selectedSymbolForChart}
      />
    </div>
  );
};

export default PortfolioPage;

